//@flow
import React, { useState, useCallback } from "react";
import modal from "../js/Modal/components/StandardModal.react";
import Constants from "../js/constants";
import { FaSearch, FaTimes, FaSpinner } from "react-icons/fa";
import axios, { AxiosResponse, AxiosError } from "axios";
import Link from "./Link";
import UrlData from "../js/Utils/url-data";
import { debounce } from 'lodash';
import type {ProductSearchResult} from '../js/Stores/Products/productModel';

type Props = {
  close?:any,
};

const isKeyPressEnter = (code:number):boolean => {
  return code === 13;
};

const filterSearchResults = (results?:ProductSearchResult[]) => {
  if (!results) return [];
  return results.filter(product => product.type !== 'snackpass');
};

const getResultUrl = (result) => {
  const host = UrlData.getFullHostname();
  const url = result.url !== "" ? `${host}${result.url}` : `${host}/product/${result.url_key}`;
  return url;
};

const SearchModal = (props:Props) => {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const debouncedHandledSearch = useCallback(
    debounce((query:any) => {
      fetchResults(query);
    }, 200, {leading: false, trailing: true, maxWait: 500}), []
  );

  function onChange(event:any) {
    debouncedHandledSearch(event.target.value);
  }

  function onKeyDown(event:any) {
    if (isKeyPressEnter(event.keyCode)) {
      debouncedHandledSearch.flush();
    }
  }

  function onClose() {
    if (props.close !== undefined) props.close();
  }

  function fetchResults(query:any) {
    if (!query) return;
    const config = {
      method: "get",
      url: `/ipa/v2/membership/search/${encodeURI(query)}`,
    };
    setIsLoading(true);
    axios(config)
      .then((response:AxiosResponse) => {
        const results = filterSearchResults(response.data.body);
        setResults(results);
        setIsLoading(false);
        setHasSearched(true);
        return true;
      })
      .catch((error:AxiosError) => {
        console.log("error", error);
        setIsLoading(false);
        setHasSearched(true);
      });
  }

  function _getSearchOrSpinner() {
    if (isLoading) {
      return (
        <FaSpinner
          size="20"
          className="search-spinner"
          ariaLabel="Loading results"
        />
      );
    }

    return (
      <FaSearch
        spin={true}
        size="20"
        className="search-button"
        ariaLabel="Search"
        onClick={fetchResults}
      />
    );
  }

  const _emptyMessage = () => {
    if (hasSearched && (!results || results.length === 0)) {
      return <div className="empty-results">No search results found!</div>;
    }
  };

  // TODO: if `isLoading`, show a loading spinner
  // instead of search icon or something similar

  return (
    <div className="search-modal-content">
      <div className="search-modal-header-container">
        <div className="search-button-container">{_getSearchOrSpinner()}</div>
        <div className="input-container">
          <input
            aria-label="Search snacks"
            className="search-modal-input"
            title="Search snacks"
            placeholder="Search snacks"
            onChange={onChange}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="close-button-container">
          <FaTimes
            size="24"
            className="close-button"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
      </div>
      <div className="results-container">
        {_emptyMessage()}
        {results.map((result) => (
          <li className="search-modal-result" key={result.id}>
            <Link
              to={getResultUrl(result)}
              external={true}
              className="search-modal-result-link"
            >
              {result.name}
            </Link>
          </li>
        ))}
      </div>
    </div>
  );
};

export default modal<Props>(SearchModal, {
  name: Constants.SEARCH_MODAL,
  className: "search-modal",
});
